import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { ForecastListItem } from '@futrli-sdk/predict';
import { CustomPopover } from '@futrli/ui-components';

import Icon from 'carbon-react/lib/components/icon';

import * as styles from './performance-overview.scss';
import { Typography } from 'carbon-react/lib/components/typography/typography.component';

const PRIOR_PERIOD_VALUE = 'prior_period';

interface ShowPerformanceSelectProps {
  selectedValue: string;
  forecasts: ForecastListItem[];
  onChange: (value: string) => void;
  helpLinkTo?: string;
}

export const ShowPerformanceSelect = ({
  selectedValue,
  forecasts,
  onChange,
  helpLinkTo
}: ShowPerformanceSelectProps) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => setAnchorElement(null);

  const handleSelect = (value: string) => {
    onChange(value);
    handleClose();
  };

  const selectedForecast = forecasts.find(f => f.id === selectedValue) || forecasts[0];

  useEffect(() => {
    if (!forecasts.length) {
      return;
    }
    if (!forecasts.find(f => f.id === selectedValue)) {
      onChange(forecasts[0].id);
    }
  }, [forecasts, selectedValue, onChange]);

  return (
    <div style={{ marginBottom: 16 }}>
      <Typography fontSize="14px" fontWeight="500" lineHeight="140%" mb={1}>
        <FormattedMessage
          id="predict.performance_overview.performance_to_targets_of"
          defaultMessage="Performance to targets of"
        />
      </Typography>
      <div className={styles.selectTrigger} onClick={handleClick}>
        <span>{selectedForecast?.name || ''}</span>
        <Icon type="caret_large_down" />
      </div>
      <CustomPopover
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        onClose={handleClose}
        id="show-performance-select"
        offset={{ x: 0, y: 4 }}
        style={{ padding: 0 }}
      >
        <div className={styles.dropdownContainer}>
          {/* Forecast options */}
          {forecasts.length > 0 ? (
            forecasts.map(forecast => {
              const isSelected = selectedValue === forecast.id;
              return (
                <div
                  key={forecast.id}
                  className={`${styles.dropdownOption} ${isSelected ? styles.dropdownOptionSelected : ''}`}
                  onClick={() => handleSelect(forecast.id)}
                >
                  <span className={styles.dropdownCheckmark}>
                    {isSelected ? <Icon type="tick" fontSize="small" /> : ''}
                  </span>
                  <span>
                    <FormattedMessage
                      id="predict.performance_overview.versus_targets_of"
                      defaultMessage="Versus targets of {forecastName}"
                      values={{ forecastName: forecast.name }}
                    />
                  </span>
                </div>
              );
            })
          ) : (
            <>
              <div className={styles.dropdownDisabledText}>
                <FormattedMessage
                  id="predict.performance_overview.no_targets_setup"
                  defaultMessage="No targets setup"
                />
              </div>
              {helpLinkTo && (
                <Link to={helpLinkTo} className={styles.dropdownHelpLink} onClick={handleClose}>
                  <span className={styles.helpIcon}>{'?'}</span>
                  <FormattedMessage
                    id="predict.performance_overview.help_setting_up_targets"
                    defaultMessage="Help setting up targets"
                  />
                </Link>
              )}
            </>
          )}
        </div>
      </CustomPopover>
    </div>
  );
};

export { PRIOR_PERIOD_VALUE };
